import React, { useEffect, useState } from "react";
import {
    Box,
    TextField,
    Button,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import PageHeader1 from "../../components/common/PageHeader1";
import axios from "axios";
import { handleUnauthorized } from "../../TokenAuth/auth";
import { formatUTCDate } from "../../utils/dateUtils";
import { toast } from 'react-toastify';
import * as XLSX from "xlsx";

function PoojaReport() {
    const [rowsData, setRowsData] = useState([]);
    const [loading, setLoading] = useState(false);

    const [pagination, setPagination] = useState({
        page: 0,
        pageSize: 10,
    });
    const [total, setTotal] = useState(0);

    // 🔍 SEARCH
    const [searchType, setSearchType] = useState("poojaName");
    const [searchValue, setSearchValue] = useState("");
    const [appliedSearchType, setAppliedSearchType] = useState("poojaName");
    const [appliedSearchValue, setAppliedSearchValue] = useState("");

    // 🔍 STATUS FILTER
    const [status, setStatus] = useState("Completed");
    const [appliedStatus, setAppliedStatus] = useState("Completed");

    // 📅 DATE FILTER
    const todayStr = new Date().toISOString().split("T")[0];
    const [isDefaultToday, setIsDefaultToday] = useState(true);
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");
    const [appliedStartDate, setAppliedStartDate] = useState("");
    const [appliedEndDate, setAppliedEndDate] = useState("");

    const [isExporting, setIsExporting] = useState(false);

    const token = localStorage.getItem("astroVani-admin-token");
    const BASE_URL = process.env.REACT_APP_BASEURL;

    // ================= API =================
    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                const params = {
                    page: pagination.page + 1,
                    limit: pagination.pageSize,
                };

                // 🔍 SEARCH PARAM
                if (appliedSearchValue) {
                    params[appliedSearchType] = appliedSearchValue;
                }
                if (appliedStatus !== "All") {
                    params.status = appliedStatus;
                }

                const effectiveStartDate = appliedStartDate ? appliedStartDate : (isDefaultToday ? todayStr : "");
                const effectiveEndDate = appliedEndDate ? appliedEndDate : (isDefaultToday ? todayStr : "");

                if (effectiveStartDate) {
                    params.startDate = effectiveStartDate;
                }
                if (effectiveEndDate) {
                    params.endDate = effectiveEndDate;
                }

                const res = await axios.get(
                    `${BASE_URL}/admin/pooja/report`,
                    {
                        params,
                        headers: { Authorization: token },
                    }
                );

                setRowsData(res?.data?.data || []);
                setTotal(res?.data?.paginationDetail?.totalRecords || 0);
            } catch (err) {
                if (err?.response?.status === 401) handleUnauthorized();
                console.error(err);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [pagination, appliedSearchType, appliedSearchValue, appliedStatus, appliedStartDate, appliedEndDate, isDefaultToday, todayStr, BASE_URL, token]);

    // ================= APPLY =================
    const handleApply = () => {
        if (startDate && endDate && new Date(endDate) < new Date(startDate)) {
            toast.error("End Date must be greater than or equal to Start Date");
            return;
        }

        const hasSearch = Boolean(searchValue.trim());
        const hasDates = Boolean(startDate || endDate);

        if (hasSearch || hasDates) {
            setIsDefaultToday(false);
        } else {
            setIsDefaultToday(true);
        }

        setAppliedSearchType(searchType);
        setAppliedSearchValue(searchValue);
        setAppliedStatus(status);
        setAppliedStartDate(startDate);
        setAppliedEndDate(endDate);
        setPagination((prev) => ({ ...prev, page: 0 }));
    };

    // ================= RESET =================
    const handleReset = () => {
        setSearchType("poojaName");
        setSearchValue("");
        setAppliedSearchType("poojaName");
        setAppliedSearchValue("");
        setStatus("Completed");
        setAppliedStatus("Completed");
        setStartDate("");
        setEndDate("");
        setAppliedStartDate("");
        setAppliedEndDate("");
        setIsDefaultToday(true);
        setPagination({ page: 0, pageSize: 10 });
    };

    // ================= EXPORT =================
    const handleExport = async () => {
        setIsExporting(true);
        try {
            const params = new URLSearchParams();

            if (appliedSearchValue) {
                params.append(appliedSearchType, appliedSearchValue);
            }
            if (appliedStatus !== "All") {
                params.append("status", appliedStatus);
            }

            const effectiveStartDate = appliedStartDate ? appliedStartDate : (isDefaultToday ? todayStr : "");
            const effectiveEndDate = appliedEndDate ? appliedEndDate : (isDefaultToday ? todayStr : "");

            if (effectiveStartDate) {
                params.append("startDate", effectiveStartDate);
            }
            if (effectiveEndDate) {
                params.append("endDate", effectiveEndDate);
            }

            const response = await fetch(
                `${BASE_URL}/admin/pooja/reportExport?${params.toString()}`,
                {
                    headers: { Authorization: token },
                }
            );

            if (!response.ok) {
                if (response.status === 401) handleUnauthorized();
                throw new Error("Export failed");
            }

            const result = await response.json();
            const data = result?.data || result || [];

            const exportData = data.map((item, i) => ({
                "S.No": i + 1,
                "Order ID": item.orderId ? String(item.orderId) : "-",
                "User Name": item.userName || "Deleted User",
                "Email": item.email || "-",
                "Mobile Number": item.mobileNumber ? String(item.mobileNumber) : "-",
                "Pooja Name": item.poojaName || "-",
                "Astrologer Name": item.astrologerName || "-",
                "Status": item.status || "-",
                "Pooja Price": item.poojaPrice ? Number(item.poojaPrice) : 0,
                "GST Amount": item.gstAmount ? Number(item.gstAmount) : 0,
                "Total Paid": Number(item.poojaPrice || 0) + Number(item.gstAmount || 0),
                "Astrologer Earnings": item.astrologerEarnings ? Number(item.astrologerEarnings) : 0,
                "Astrologer Referral Earnings": item.sharingAstrologerEarnings ? Number(item.sharingAstrologerEarnings) : 0,
                "Company Earnings": item.companyEarnings ? Number(item.companyEarnings) : 0,
                "Prasad Delivery": item.prasadDelivery ? "Yes" : "No",
                "Payment Status": item.paymentStatus || "-",
                "Payment Mode": item.paymentMode || "-",
                "Wallet Credit Applied": item.walletCreditApplied ? Number(item.walletCreditApplied) : 0,
                "Remaining Amount": item.remainingAmount ? Number(item.remainingAmount) : 0,
                "Rejection Reason": item.rejectionReason || "-",
                "Created Date": formatUTCDate(item.createdAt, { format: "date" }),
                "Created Time": formatUTCDate(item.createdAt, { format: "time" }),
            }));

            const worksheet = XLSX.utils.json_to_sheet(exportData);

            // Set explicit column widths to prevent '#####'
            worksheet["!cols"] = [
                { wch: 8 },  // S.No
                { wch: 25 }, // Order ID
                { wch: 20 }, // User Name
                { wch: 24 }, // Email
                { wch: 18 }, // Mobile Number
                { wch: 22 }, // Pooja Name
                { wch: 20 }, // Astrologer Name
                { wch: 14 }, // Status
                { wch: 14 }, // Pooja Price
                { wch: 14 }, // GST Amount
                { wch: 14 }, // Total Paid
                { wch: 20 }, // Astrologer Earnings
                { wch: 26 }, // Astrologer Referral Earnings
                { wch: 18 }, // Company Earnings
                { wch: 16 }, // Prasad Delivery
                { wch: 16 }, // Payment Status
                { wch: 16 }, // Payment Mode
                { wch: 22 }, // Wallet Credit Applied
                { wch: 18 }, // Remaining Amount
                { wch: 24 }, // Rejection Reason
                { wch: 16 }, // Created Date
                { wch: 16 }, // Created Time
            ];

            // Explicitly force Order ID (Col B) and Mobile Number (Col E) as text strings to prevent scientific notation & precision loss
            Object.keys(worksheet).forEach((cellKey) => {
                if (cellKey.startsWith("!")) return;
                const col = cellKey.replace(/[0-9]/g, "");
                if ((col === "B" || col === "E") && cellKey !== "B1" && cellKey !== "E1") {
                    const cell = worksheet[cellKey];
                    if (cell) {
                        cell.t = "s";
                        cell.z = "@";
                        cell.v = String(cell.v ?? "");
                    }
                }
            });

            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, "PoojaReport");

            const today = new Date().toISOString().split("T")[0];
            XLSX.writeFile(workbook, `pooja-report_${today}.xlsx`);
            toast.success("Pooja report exported successfully!");
        } catch (err) {
            console.error(err);
            toast.error("Export failed");
        } finally {
            setIsExporting(false);
        }
    };

    // ================= COLUMNS =================
    const columns = [
        { field: "sono", headerName: "S.No", width: 80, sortable: false, filterable: false, renderCell: (params) => params.api.getAllRowIds().indexOf(params.id) + 1 + pagination.page * pagination.pageSize },
        { field: "orderId", headerName: "Order ID", width: 180 },
        {
            field: "userName", headerName: "User Name", width: 180,
            renderCell: (params) => params.value || "Deleted User"
        },
        {
            field: "email", headerName: "Email", width: 180,
            renderCell: (params) => params.value || "-"
        },
        {
            field: "mobileNumber", headerName: "Mobile", width: 130,
            renderCell: (params) => params.value || "-"
        },
        { field: "poojaName", headerName: "Pooja Name", width: 180 },
        { field: "astrologerName", headerName: "Astrologer Name", width: 180 },
        { field: "status", headerName: "Status", width: 120 },
        { field: "poojaPrice", headerName: "Pooja Price", width: 120 },
        { field: "gstAmount", headerName: "GST Amount", width: 120 },
        { field: "totalPaid", headerName: "Total Paid", width: 120, renderCell: (params) => Number(params.row.poojaPrice || 0) + Number(params.row.gstAmount || 0) },
        { field: "astrologerEarnings", headerName: "Astro Earnings", width: 140 },
        {
            field: "sharingAstrologerEarnings",
            headerName: "Astrologer Referral Earnings",
            width: 210,
            renderCell: (params) => (params.value !== undefined && params.value !== null ? params.value : "0")
        },
        { field: "companyEarnings", headerName: "Company Earnings", width: 140 },
        { field: "prasadDelivery", headerName: "Prasad Delivery", width: 130, renderCell: (params) => params.value ? "Yes" : "No" },
        { field: "paymentStatus", headerName: "Payment Status", width: 140 },
        { field: "paymentMode", headerName: "Payment Mode", width: 130 },
        { field: "walletCreditApplied", headerName: "Wallet Applied", width: 140 },
        { field: "remainingAmount", headerName: "Remaining Amount", width: 150 },
        { field: "rejectionReason", headerName: "Rejection Reason", width: 200, renderCell: (params) => params.value || "-" },
        {
            field: "createdAt",
            headerName: "Created Date",
            width: 140,
            renderCell: (params) => params.value ? formatUTCDate(params.value, { format: "date" }) : "-"
        },
        {
            field: "createdTime",
            headerName: "Created Time",
            width: 130,
            renderCell: (params) => params.row.createdAt ? formatUTCDate(params.row.createdAt, { format: "time" }) : "-"
        },
    ];

    return (
        <div className="container-xxl">
            <PageHeader1 pagetitle="Pooja Report" />
            <div className="row g-3 mb-3">
                <div className="col-12">
                    <div className="card">
                        <div className="card-body">
                            {/* SEARCH BAR */}
                            <Box sx={{ display: "flex", gap: 2, mb: 3, flexWrap: "wrap", alignItems: "center" }}>
                                <FormControl size="small" sx={{ minWidth: 200 }}>
                                    <InputLabel id="search-type-label" sx={{ top: '-4px' }}>Search By</InputLabel>
                                    <Select
                                        labelId="search-type-label"
                                        value={searchType}
                                        label="Search By"
                                        onChange={(e) => {
                                            setSearchType(e.target.value);
                                            setSearchValue("");
                                        }}
                                        sx={{ height: 35 }}
                                    >
                                        <MenuItem value="poojaName">Pooja Name</MenuItem>
                                        <MenuItem value="orderId">Order ID</MenuItem>
                                        <MenuItem value="mobileNumber">Mobile Number</MenuItem>
                                        <MenuItem value="userName">User Name</MenuItem>
                                        <MenuItem value="astrologerName">Astrologer Name</MenuItem>
                                    </Select>
                                </FormControl>
                                <TextField
                                    label="Search Value"
                                    value={searchValue}
                                    size="small"
                                    onChange={(e) => setSearchValue(e.target.value)}
                                    InputProps={{ sx: { height: 35 } }}
                                    InputLabelProps={{ sx: { top: '-4px' } }}
                                    sx={{ minWidth: 250 }}
                                />

                                <TextField
                                    label="Start Date"
                                    type="date"
                                    value={startDate}
                                    size="small"
                                    onChange={(e) => setStartDate(e.target.value)}
                                    InputProps={{ sx: { height: 35 } }}
                                    InputLabelProps={{ shrink: true, sx: { top: '-4px' } }}
                                    sx={{ minWidth: 150 }}
                                />
                                <TextField
                                    label="End Date"
                                    type="date"
                                    value={endDate}
                                    size="small"
                                    onChange={(e) => setEndDate(e.target.value)}
                                    InputProps={{ sx: { height: 35 } }}
                                    InputLabelProps={{ shrink: true, sx: { top: '-4px' } }}
                                    sx={{ minWidth: 150 }}
                                />

                                {/* <FormControl size="small" sx={{ minWidth: 150 }}>
                                    <InputLabel id="status-label" sx={{ top: '-4px' }}>Status</InputLabel>
                                    <Select
                                        labelId="status-label"
                                        value={status}
                                        label="Status"
                                        onChange={(e) => setStatus(e.target.value)}
                                        sx={{ height: 35 }}
                                    >
                                        <MenuItem value="All">All</MenuItem>
                                        <MenuItem value="Pending">Pending</MenuItem>
                                        <MenuItem value="Accepted">Accepted</MenuItem>
                                        <MenuItem value="Scheduled">Scheduled</MenuItem>
                                        <MenuItem value="Ongoing">Ongoing</MenuItem>
                                        <MenuItem value="Completed">Completed</MenuItem>
                                        <MenuItem value="Cancelled">Cancelled</MenuItem>
                                        <MenuItem value="Rejected">Rejected</MenuItem>
                                        <MenuItem value="Failed">Failed</MenuItem>
                                        <MenuItem value="Expired">Expired</MenuItem>
                                    </Select>
                                </FormControl> */}

                                <Button
                                    variant="contained"
                                    onClick={handleApply}
                                    sx={{ minWidth: 100, height: 35 }}
                                >
                                    Apply
                                </Button>
                                <Button
                                    variant="outlined"
                                    onClick={handleReset}
                                    sx={{ minWidth: 100, height: 35 }}
                                >
                                    Reset
                                </Button>

                                <Box sx={{ flexGrow: 1 }} />
                                <Button
                                    variant="contained"
                                    color="success"
                                    onClick={handleExport}
                                    disabled={isExporting}
                                    sx={{ height: 35 }}
                                >
                                    {isExporting ? "Exporting..." : "Export"}
                                </Button>
                            </Box>

                            {/* DATA GRID */}
                            <Box sx={{ height: 600, width: "100%" }}>
                                <DataGrid
                                    rows={rowsData}
                                    columns={columns}
                                    getRowId={(row) => row._id || Math.random()}
                                    rowCount={total}
                                    loading={loading}
                                    pageSizeOptions={[10, 25, 50, 100]}
                                    paginationModel={pagination}
                                    paginationMode="server"
                                    onPaginationModelChange={setPagination}
                                    disableRowSelectionOnClick
                                />
                            </Box>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default PoojaReport;
